import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Loader2, ArrowRight, CheckCircle2, AlertTriangle, Info, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, CCButton, StatusBadge } from "@/command/ccUi";

const EXAMPLES = [
  "What's our current net cash and revenue?",
  "How many people are active and who's on leave?",
  "Summarise ConstructONS.AI product health.",
  "Which invoices are overdue?",
];

const SEV_ICON = { info: Info, warning: AlertTriangle, critical: AlertCircle };

// Minimal safe markdown: **bold**, bullet lines, paragraphs.
function renderAnswer(text) {
  const lines = (text || "").split("\n");
  return lines.map((ln, i) => {
    const bulleted = /^\s*[-*]\s+/.test(ln);
    const content = ln.replace(/^\s*[-*]\s+/, "");
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-ink font-semibold">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
    );
    if (!ln.trim()) return <div key={i} className="h-2" />;
    return bulleted
      ? <div key={i} className="flex gap-2 text-ink"><span className="text-oraya-accent mt-1">·</span><span>{parts}</span></div>
      : <p key={i} className="text-ink">{parts}</p>;
  });
}

export default function Intelligence() {
  const { can } = useCommand();
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [actionDone, setActionDone] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const endRef = useRef(null);

  const loadAlerts = useCallback(() => { api.get("/cc/intelligence").then(({ data }) => setAlerts(data)).catch(() => {}); }, []);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useRealtime(["approvals", "notification", "people", "finance"], loadAlerts);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setAsking(true); setResult(null); setActionDone(false);
    try {
      const { data } = await api.post("/cc/intelligence/ask", { question: text });
      setResult({ question: text, ...data });
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "AI request failed");
    } finally { setAsking(false); }
  };

  const confirmAction = async () => {
    setConfirming(true);
    try {
      await api.post("/cc/intelligence/action", { action: result.proposed_action });
      toast.success("Approval created"); setActionDone(true); loadAlerts();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-oraya-accent" />
        <h1 className="display text-2xl md:text-3xl font-light text-ink">ORAYA Intelligence</h1>
      </div>
      <p className="text-subtle mt-1 mb-6">Ask questions over your real company data. Answers are grounded, sourced and never invented.</p>

      {/* Command bar */}
      <div className="glass-heavy rounded-2xl p-2 flex items-center gap-2 mb-4" data-testid="ai-command-bar">
        <Sparkles className="w-4 h-4 text-oraya-accent ml-3 shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ask ORAYA anything about the company…" data-testid="ai-input"
          className="flex-1 bg-transparent text-ink placeholder:text-subtle/60 focus:outline-none py-2.5 text-sm" />
        <CCButton onClick={() => ask()} disabled={asking || !q.trim()} testid="ai-ask-btn">
          {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
        </CCButton>
      </div>

      {!result && !asking && (
        <div className="flex flex-wrap gap-2 mb-8">
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => { setQ(ex); ask(ex); }} data-testid="ai-example"
              className="px-3.5 py-2 rounded-full text-xs glass-medium text-subtle hover:text-ink transition-colors">{ex}</button>
          ))}
        </div>
      )}

      {asking && (
        <Panel className="mb-8"><div className="flex items-center gap-2 text-subtle py-4"><Loader2 className="w-4 h-4 animate-spin" /> ORAYA is analysing your company data…</div></Panel>
      )}

      {result && (
        <div className="mb-8" data-testid="ai-result">
          <p className="text-sm text-subtle mb-2">You asked</p>
          <p className="text-ink mb-4 display text-lg">{result.question}</p>
          <Panel testid="ai-answer">
            <div className="space-y-1 leading-relaxed text-sm">{renderAnswer(result.answer)}</div>

            {result.sources?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-hairline/[0.08]">
                <p className="text-xs uppercase tracking-[0.16em] text-subtle mb-2">Sources</p>
                <div className="flex flex-wrap gap-2">
                  {result.sources.map((s, i) => (
                    <Link key={i} to={s.link || "#"} data-testid={`ai-source-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs glass-medium text-ink hover:border-hairline/25">
                      <FileText className="w-3 h-3" /> {s.source}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          {result.proposed_action && can("approvals", "CREATE") && (
            <Panel className="mt-4 border border-oraya-accent/20" testid="ai-proposed-action">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-oraya-accent mt-0.5" />
                <div className="flex-1">
                  <p className="text-ink font-medium">Suggested action · Create approval</p>
                  <p className="text-sm text-ink mt-1">{result.proposed_action.title}</p>
                  {result.proposed_action.description && <p className="text-sm text-subtle mt-1">{result.proposed_action.description}</p>}
                  {result.proposed_action.amount != null && <p className="text-sm text-subtle mt-1">Amount: ₹{Number(result.proposed_action.amount).toLocaleString("en-IN")}</p>}
                  <div className="mt-4">
                    {actionDone ? (
                      <StatusBadge status="healthy" label="Approval created" />
                    ) : (
                      <CCButton onClick={confirmAction} disabled={confirming} testid="ai-confirm-action">
                        {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Confirm &amp; create approval
                      </CCButton>
                    )}
                  </div>
                </div>
              </div>
            </Panel>
          )}
          <div ref={endRef} />
        </div>
      )}

      {/* Live signals */}
      <Panel title="Live signals" testid="ai-signals"
        action={<span className="text-xs text-subtle">{alerts?.count || 0} require attention</span>}>
        {(!alerts || alerts.count === 0) ? (
          <p className="text-subtle text-sm py-6 text-center">Nothing needs your attention right now.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {alerts.alerts.map((a, i) => {
              const Icon = SEV_ICON[a.severity] || Info;
              return (
                <Link key={i} to={a.link || "#"} data-testid={`signal-${i}`} className="flex items-center gap-3 p-3 rounded-xl glass-medium hover:border-hairline/25 transition-colors">
                  <Icon className={`w-4 h-4 ${a.severity === "critical" ? "text-red-500" : a.severity === "warning" ? "text-amber-500" : "text-oraya-accent"}`} />
                  <span className="text-sm text-ink flex-1">{a.text}</span>
                  <StatusBadge status={a.severity} label={a.source} />
                  <ArrowRight className="w-4 h-4 text-subtle" />
                </Link>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
